import { useMemo, useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { supabase } from '@/lib/supabase'
import { isOptlk } from '@/lib/optimistic'
import { alertDialog } from '@/components/Dialogs'
import Modal from '@/components/Modal'
import RunSelector from '@/components/RunSelector'
import {
  canSubmitMove, invalidateAfterMove, moveCount, moveSnapshot, runMoveRpc,
  varietyChanges, type MoveMode,
} from '@/lib/movePallet'
import type { Run } from '@/types/database'
import { ArrowLeftRight, AlertTriangle } from 'lucide-react'

// Move pallet(s) to a different run (mig 810 + 820). Admin-only correction for
// the "created on the wrong run" mistake — including a WRONG-VARIETY run (the
// way ops "change a pallet's variety"). Target runs are restricted to the same
// COMMODITY (a hard guard, server-enforced); variety may differ and is
// soft-warned. On a cross-variety move the server re-snapshots the pallet's
// PPECB variety_code from the target run.
//
// Dispatched / consignment / mission pallets are normally locked; an admin can
// override via an explicit confirm (requiresForce), which sends p_force=true.
//
// Two modes share one dialog:
//   single  — one pallet, optimistic-concurrency checked (needs its version).
//   bulk    — several pallets, all-or-nothing server-side.
//
// The decisions (can-submit, snapshot, which RPC, what to invalidate) live in
// `@/lib/movePallet`; what is left here is composition.

interface Props {
  mode:           MoveMode
  /** Pallet's current run — used to filter eligible targets + exclude itself. */
  currentRunId:   string
  commodity:      string | null
  /** Pallet's current variety — only for the change warning (not a filter). */
  currentVariety: string | null
  /** True if any pallet being moved is dispatched / on a consignment / mission. */
  requiresForce?: boolean
  onClose:        () => void
  /** Optional: fired after a successful move (e.g. clear bulk selection). */
  onMoved?:       (target: Run | null) => void
}

/** Eligible targets: same COMMODITY (variety may differ), live, not archived,
 *  not the current run. Server enforces the same commodity guard. */
function useEligibleRuns(commodity: string | null, currentRunId: string) {
  return useQuery({
    queryKey: ['move-pallet:eligible-runs', commodity, currentRunId],
    enabled: !!commodity,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('runs')
        .select('run_id, run_number, grower, grower_id, puc, variety, commodity, status, packout_percent, tipped_net_weight_kg, pallet_count')
        .eq('commodity', commodity!)
        .in('status', ['ACTIVE', 'TIPPING_COMPLETE', 'COMPLETE'])
        .is('deleted_at', null)
        .neq('run_id', currentRunId)
        .order('started_at', { ascending: false })
        .limit(60)
      if (error) throw error
      return (data ?? []) as unknown as Run[]
    },
  })
}

function MoveTitle({ count, commodity, currentVariety }: {
  count: number; commodity: string | null; currentVariety: string | null
}) {
  return (
    <span className="flex items-center gap-2">
      <ArrowLeftRight size={16} className="text-blue-400 shrink-0" />
      <span className="truncate">
        Move {count} pallet{count === 1 ? '' : 's'} to run
        {commodity && (
          <span className="text-xs font-normal text-fg-muted ml-2">
            {commodity}{currentVariety ? ` · ${currentVariety}` : ''}
          </span>
        )}
      </span>
    </span>
  )
}

function NoCommodityWarning() {
  return (
    <p className="text-xs text-amber-300 flex items-start gap-1.5 leading-snug">
      <AlertTriangle size={12} className="mt-0.5 shrink-0" />
      This run has no commodity set, so eligible targets can't be matched. Set the commodity on the run first.
    </p>
  )
}

/** Soft warning: the move changes the pallet's variety. */
function VarietyChangeWarning({ from, to }: { from: string; to: string | null }) {
  return (
    <p className="text-xs text-amber-300 flex items-start gap-1.5 leading-snug bg-amber-900/20 border border-amber-800/50 rounded-lg px-3 py-2">
      <AlertTriangle size={12} className="mt-0.5 shrink-0" />
      <span>
        Variety changes: <b>{from}</b> → <b>{to}</b>.
        The pallet's PPECB variety code updates to match the destination run.
      </span>
    </p>
  )
}

/** Hard warning + required confirm: moving dispatched / consigned pallets. */
function ForceConfirm({ count, confirmed, onConfirm }: {
  count: number; confirmed: boolean; onConfirm: (v: boolean) => void
}) {
  const one = count === 1
  return (
    <div className="text-xs text-red-200 bg-red-900/25 border border-red-800/60 rounded-lg px-3 py-2.5 space-y-2">
      <p className="flex items-start gap-1.5 leading-snug">
        <AlertTriangle size={13} className="mt-0.5 shrink-0 text-red-400" />
        <span>
          {one ? 'This pallet is' : 'One or more of these pallets are'} <b>dispatched or on a
          consignment / mission</b>. Moving {one ? 'it' : 'them'} corrects your records here, but a
          consignment already submitted to PPECB will <b>not</b> auto-update — handle that via PPECB's own
          recall / amendment.
        </span>
      </p>
      <label className="flex items-center gap-2 cursor-pointer pl-5">
        <input type="checkbox" checked={confirmed}
          onChange={e => onConfirm(e.target.checked)}
          className="h-4 w-4 accent-red-500" />
        <span>I understand — move {one ? 'it' : 'them'} anyway</span>
      </label>
    </div>
  )
}

function DestinationPicker({ commodity, isLoading, runs, selectedRun, onSelect }: {
  commodity: string | null
  isLoading: boolean
  runs: Run[]
  selectedRun: Run | null
  onSelect: (id: string) => void
}) {
  return (
    <div>
      <label className="text-xs font-semibold text-fg-muted uppercase tracking-wider">
        Destination run
      </label>
      <p className="text-[11px] text-fg-muted mb-1.5">
        Runs of the same commodity ({commodity ?? '—'}). Variety may differ.
      </p>
      {isLoading ? (
        <p className="text-sm text-fg-muted py-3">Loading runs…</p>
      ) : (
        <div className="rounded-xl overflow-hidden border hairline">
          <RunSelector
            runs={runs}
            selected={selectedRun}
            onSelect={onSelect}
            emptyLabel={`No other ${commodity ?? ''} runs to move to`}
          />
        </div>
      )}
    </div>
  )
}

function ReasonField({ value, onChange }: { value: string; onChange: (v: string) => void }) {
  return (
    <div>
      <label className="text-xs font-semibold text-fg-muted uppercase tracking-wider">
        Reason <span className="text-red-400">*</span>
      <textarea
        value={value}
        onChange={e => onChange(e.target.value)}
        rows={2}
        placeholder="e.g. logged on the wrong run / wrong variety at packing"
        className="mt-1 w-full surface border hairline-strong rounded-lg px-3 py-2 text-sm text-fg placeholder-gray-500 focus:outline-none focus:border-blue-500"
      />
      </label>
      <p className="text-[11px] text-fg-muted mt-1">Kept on the audit trail.</p>
    </div>
  )
}

function MoveFooter({ count, disabled, isPending, onCancel }: {
  count: number; disabled: boolean; isPending: boolean; onCancel: () => void
}) {
  return (
    <footer className="px-4 py-3 border-t hairline flex items-center justify-end gap-2">
      <button type="button" onClick={onCancel}
        className="text-xs px-3 py-2 rounded-lg text-fg-muted hover:bg-surface">
        Cancel
      </button>
      <button
        type="submit"
        disabled={disabled}
        className="text-xs font-semibold px-4 py-2 rounded-lg bg-blue-600 text-white hover:bg-blue-500 disabled:opacity-40 disabled:cursor-not-allowed flex items-center gap-1.5">
        <ArrowLeftRight size={14} />
        {isPending ? 'Moving…' : `Move ${count} pallet${count === 1 ? '' : 's'}`}
      </button>
    </footer>
  )
}

/** Everything between the title bar and the footer. Lives here rather than
 *  inline so the dialog itself stays composition — the warnings are the only
 *  conditional part of this screen. */
function MoveDialogBody({
  commodity, currentVariety, isLoading, runs, selectedRun, onSelectRun,
  count, requiresForce, forceConfirmed, onForceConfirm, reason, onReason,
}: {
  commodity: string | null
  currentVariety: string | null
  isLoading: boolean
  runs: Run[]
  selectedRun: Run | null
  onSelectRun: (id: string) => void
  count: number
  requiresForce: boolean
  forceConfirmed: boolean
  onForceConfirm: (v: boolean) => void
  reason: string
  onReason: (v: string) => void
}) {
  return (
    <div className="space-y-4">
      {!commodity && <NoCommodityWarning />}

      <DestinationPicker
        commodity={commodity}
        isLoading={isLoading}
        runs={runs}
        selectedRun={selectedRun}
        onSelect={onSelectRun}
      />

      {varietyChanges(selectedRun, currentVariety) && (
        <VarietyChangeWarning from={currentVariety!} to={selectedRun!.variety} />
      )}

      {requiresForce && (
        <ForceConfirm count={count} confirmed={forceConfirmed} onConfirm={onForceConfirm} />
      )}

      <ReasonField value={reason} onChange={onReason} />
    </div>
  )
}

function reportMoveFailure(e: unknown) {
  if (isOptlk(e)) {
    void alertDialog({
      title:   'Pallet changed',
      message: 'This pallet was modified by someone else. Refresh the page and try the move again.',
      tone:    'warning',
    })
    return
  }
  void alertDialog({
    title:   'Could not move',
    message: (e as { message?: string })?.message ?? 'Unknown error',
    tone:    'danger',
  })
}

export default function MovePalletDialog({
  mode, currentRunId, commodity, currentVariety, requiresForce = false, onClose, onMoved,
}: Props) {
  const qc = useQueryClient()
  const [targetRunId,    setTargetRunId]    = useState<string | null>(null)
  const [reason,         setReason]         = useState('')
  const [forceConfirmed, setForceConfirmed] = useState(false)

  const count = moveCount(mode)
  const { data: runs = [], isLoading } = useEligibleRuns(commodity, currentRunId)

  const selectedRun = useMemo(
    () => runs.find(r => r.run_id === targetRunId) ?? null,
    [runs, targetRunId],
  )

  const canSubmit = canSubmitMove({ targetRunId, reason, requiresForce, forceConfirmed })

  const move = useMutation({
    mutationFn: () => runMoveRpc(supabase, { mode, targetRunId, reason, requiresForce }),
    onSuccess: () => {
      invalidateAfterMove(qc, mode)
      onMoved?.(selectedRun)
      onClose()
    },
    onError: reportMoveFailure,
  })

  return (
    <Modal
      onClose={onClose}
      snapshot={moveSnapshot({ targetRunId, reason, forceConfirmed })}
      onSubmit={() => { if (canSubmit && !move.isPending) move.mutate() }}
      size="lg"
      title={<MoveTitle count={count} commodity={commodity} currentVariety={currentVariety} />}
      footer={attemptClose => (
        <MoveFooter
          count={count}
          disabled={!canSubmit || move.isPending}
          isPending={move.isPending}
          onCancel={attemptClose}
        />
      )}>
      <MoveDialogBody
        commodity={commodity}
        currentVariety={currentVariety}
        isLoading={isLoading}
        runs={runs}
        selectedRun={selectedRun}
        onSelectRun={setTargetRunId}
        count={count}
        requiresForce={requiresForce}
        forceConfirmed={forceConfirmed}
        onForceConfirm={setForceConfirmed}
        reason={reason}
        onReason={setReason}
      />
    </Modal>
  )
}
